// ABOUTME: The React Query hooks behind the interview card: the card itself, and the commands, each of which answers
// ABOUTME: with the card as it reads afterwards and so seeds the cache without a second read.
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ApiError } from 'utils/api';
import { INTERVIEW_CARD_QUERY_KEY } from './constants';
import { InterviewApi, InterviewCardData } from './types';

/**
 * Scoped to the credential as well as the interview, so a card cached for one signed-in talent is never shown to the
 * next; the commands write their answer under this same key.
 */
export const interviewCardKey = (api: InterviewApi, refId: string) => [
  INTERVIEW_CARD_QUERY_KEY,
  api.scope,
  api.identity,
  refId
];

/**
 * Codes that say the card on screen is out of date (the time was taken, the hold lapsed, the interview moved on):
 * the card is read again so the talent sees where it stands now, next to the message.
 */
const STALE_CARD_CODES = new Set([
  'error.interview.time-not-available',
  'error.interview.hold-expired',
  'error.interview.not-open',
  'error.interview.not-scheduled',
  'error.interview.too-late-to-move',
  'error.interview.move-limit',
  'error.interview.not-a-no-show',
  'error.interview.room-closed'
]);

/** The card. Not retried: a 404 is pro-core's settled answer about this interview, not a blip. */
export const useInterviewCardQuery = (api: InterviewApi, refId: string) =>
  useQuery<InterviewCardData>(
    interviewCardKey(api, refId),
    () => api.getCard(refId),
    { enabled: api.isReady, retry: false }
  );

/** One command; `run` is the api call. Its answer replaces the cached card. */
export const useInterviewCommand = <TVariables = void>(
  api: InterviewApi,
  refId: string,
  run: (variables: TVariables) => Promise<InterviewCardData>
) => {
  const queryClient = useQueryClient();
  return useMutation<InterviewCardData, unknown, TVariables>(run, {
    onSuccess: card => {
      queryClient.setQueryData(interviewCardKey(api, refId), card);
    },
    onError: error => {
      if (error instanceof ApiError && STALE_CARD_CODES.has(error.code)) {
        void queryClient.invalidateQueries(interviewCardKey(api, refId));
      }
    }
  });
};
